import { Col, Modal, Row } from "antd";
import classNames from "classnames";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { defaultTo, get, isEmpty } from "lodash";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "react-responsive";
import HelpLight from "shared/components/SVG/LMF/HelpLight";
import { NAME_SPACES } from "shared/locales/constants";
import {
  TERMINATION_MOTIFS,
  TERMINATION_RECIPIENTS,
} from "utils/api/graphql/queries/termination";
import {
  CONTACT_POINT_SYSTEM,
  DATE_FORMAT,
  FRANCE_DEPARTMENTS,
} from "utils/constants";
import { getMaxDeltaEnd, getMinDeltaStart } from "utils/helpers/array";
import { bindDateInputProps, bindInputProps } from "utils/helpers/input";
import {
  filterCityName,
  filterContractNumber,
} from "utils/helpers/validations";
import ActionButtons from "../Components/ActionButtons";
import Actions from "../Components/Actions";
import Button from "../Components/Button";
import Head from "../Components/Head";
import InfoBox from "../Components/InfoBox";
import Input from "../Components/Input";
import Layout from "../Components/Layout";
import Radio from "../Components/Radio";
import Select from "../Components/Select";
import InputChecked from "../Components/svg/check_small.svg";
import InputError from "../Components/svg/error.svg";
import HelpModal from "./HelpModal";
import UpdateCostModal from "./Widgets/UpdateCostModal";
import validation from "./validation";

const View = ({
  initialValues,
  subscription,
  onNext,
  locked,
  recalculateCost,
  project,
  onSubmitModal,
  onBack,
  submitting,
}) => {
  const [openHelp, setOpenHelp] = useState(false);
  const [occupation, setOccupation] = useState(true);
  const [resiliation, setResiliation] = useState(
    defaultTo(get(initialValues, "resiliation"), false) ||
      get(initialValues, "project.fields.replaceContract") ||
      get(initialValues, "project.fields.oldContractYear") ||
      get(initialValues, "project.fields.selfOccupation") ||
      false,
  );
  const [validStartDate, setValidStartDate] = useState();
  const [validEndDate, setValidEndDate] = useState();
  const [deltaRia, setDeltaRia] = useState(30);
  const [deltaEnd, setDeltaEnd] = useState();
  const [deltaStart, setDeltaStart] = useState(1);
  const [deltaEndRia, setDeltaEndRia] = useState();
  const [showChangeCostModal, setShowChangeCostModal] = useState({});
  const [hasOldContract, setHasOldContract] = useState(false);
  const [selfOccupation, setSelfOccupation] = useState(
    defaultTo(get(initialValues, "project.fields.selfOccupation"), true),
  );
  const [replaceContract, setReplaceContract] = useState(
    get(initialValues, "project.fields.replaceContract") || null,
  );
  const [hasOldContractMoreThanYear, setHasOldContractMoreThanYear] = useState(
    get(initialValues, "project.fields.oldContractYear") || null,
  );
  const [helpContext, setHelpContext] = useState(null);
  const [openHelpModal, setOpenHelpModal] = useState(false);
  const [helpTrigger, setHelpTrigger] = useState(null);

  const [oldIssuanceDate, setOldIssuanceDate] = useState(
    get(initialValues, "project.contract.issuanceDate"),
  );
  const { t } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const COUNTRIES = t("DETAIL.COUNTRIES", { returnObjects: true });
  const TRANSLATION = t("B2C_LMF.EFFECTIVE_DATE", { returnObjects: true });
  const [pickerValue, setPickerValue] = useState(
    !get(subscription, "project.contract.issuanceDate") && dayjs(),
  );

  useEffect(() => {
    const insurancePlans = [get(subscription, "insurancePlan")];
    const deltaRia = getMinDeltaStart({ insurancePlans, minValue: 30 });
    const deltaStart = getMinDeltaStart({
      insurancePlans,
      minValue: 1,
      key: "DELTA_START",
    });
    const deltaEndRia = getMaxDeltaEnd({ insurancePlans }) || 0;
    const deltaEnd =
      getMaxDeltaEnd({
        insurancePlans,
        key: "DELTA_END",
      }) || 0;
    setDeltaRia(deltaRia);
    setDeltaStart(deltaStart);
    setDeltaEndRia(deltaEndRia);
    setDeltaEnd(deltaEnd);
    const validEndDate = values.ria
      ? deltaEndRia === 0
        ? dayjs().endOf("year")
        : dayjs().add(deltaEndRia, "days")
      : deltaEnd === 0
        ? dayjs().endOf("year")
        : dayjs().add(deltaEnd, "days");
    const validStartDate = values.ria
      ? dayjs(
          moment
            .max(
              moment().add(deltaRia, "days").startOf("day"),
              moment().startOf("y"),
            )
            .toDate(),
        )
      : dayjs(
          moment
            .max(
              moment().add(deltaStart, "days").startOf("day"),
              moment().startOf("y"),
            )
            .toDate(),
        );
    setValidStartDate(validStartDate);
    setValidEndDate(validEndDate);
  }, []);

  const checkExpired = useCallback(({ issuanceDate }) => {
    const minIssuanceDate = dayjs().startOf("day").add(deltaRia, "days");
    const expired = dayjs(issuanceDate).isBefore(minIssuanceDate);
    return expired;
  }, []);

  const { handleSubmit, values, setFieldValue, setFieldTouched, ...formProps } =
    useFormik({
      initialValues: {
        ...initialValues,
        resiliation: defaultTo(get(initialValues, "resiliation"), false),
      },
      onSubmit: onNext,
      enableReinitialize: true,
      validationSchema: validation(
        t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, { returnObjects: true }),
        { hasRia: resiliation, occupation },
      ),
    });

  useEffect(() => {
    const replace = get(values, "project.fields.replaceContract");
    const oldYear = get(values, "project.fields.oldContractYear");
    const occupation = get(values, "project.fields.selfOccupation");
    const formResiliation = get(values, "resiliation");

    const shouldRestore = replace || oldYear || occupation || formResiliation;

    if (shouldRestore) {
      setResiliation(true);
      setFieldValue("resiliation", true);
    } else {
      setResiliation(formResiliation || false);
    }

    if (replace !== undefined) setReplaceContract(replace);
    if (oldYear !== undefined) setHasOldContractMoreThanYear(oldYear);
    if (occupation !== undefined) setSelfOccupation(occupation);
  }, []);

  const onChangeCost = () => {
    setShowChangeCostModal({});
  };

  const onCancelChangeCost = () => {
    if (showChangeCostModal?.isRia) {
      setFieldValue("resiliation", false);
      setFieldValue("project.ria", false);
      setFieldValue("project.fields.selfOccupation", undefined);
      setFieldValue(
        "project.contract.issuanceDate",
        get(initialValues, "project.contract.issuanceDate"),
      );
      setValidStartDate(dayjs().add(deltaStart, "days"));
      setPickerValue(dayjs().add(deltaStart, "days"));
    }
    recalculateCost({
      oldIssuanceDate: values?.project?.contract?.issuanceDate,
      issuanceDate: oldIssuanceDate,
      setFieldValue,
      setShowChangeCostModal,
      isCancel: true,
    });
  };

  const RIA_STATUS = {
    [true]: () => {
      setFieldValue("project.fields.selfOccupation", undefined);
      setOccupation(false);
      if (subscription?.locked) return;
      if (
        checkExpired({
          issuanceDate: values?.project?.contract?.issuanceDate,
        })
      ) {
        setValidStartDate(dayjs().add(deltaRia, "days").startOf("day"));
        recalculateCost({
          oldIssuanceDate: oldIssuanceDate,
          issuanceDate: dayjs().add(deltaRia, "days"),
          setFieldValue,
          setShowChangeCostModal,
          isRia: true,
        });
        setFieldValue(
          "project.contract.issuanceDate",
          dayjs().add(deltaRia, "days"),
        );
        setPickerValue(dayjs().add(deltaRia, "days"));
      }
    },
    [false]: () => {
      if (subscription?.locked) return;
      setFieldValue("project.ria", false);
      setFieldValue("project.fields.selfOccupation", undefined);
      setFieldValue(
        "project.contract.issuanceDate",
        get(initialValues, "project.contract.issuanceDate"),
      );
      setValidStartDate(dayjs().add(deltaStart, "days").startOf("day"));
      setPickerValue(dayjs().add(deltaStart, "days").startOf("day"));
    },
  };

  const canContinue = () => {
    const hasErrors = !isEmpty(formProps.errors);

    const resiliation = get(values, "resiliation");
    const replaceContract = get(values, "project.fields.replaceContract");
    const hasOldContractMoreThanYear = get(
      values,
      "project.fields.oldContractYear",
    );
    const selfOccupation = get(values, "project.fields.selfOccupation");

    const dueDate = get(values, "project.terminationRequest.dueDate");
    const contractNumber = get(
      values,
      "project.terminationRequest.contractNumber",
    );
    const currentCompany = get(values, "project.terminationRequest.recipient");
    const reason = get(values, "project.terminationRequest.reason");

    if (!resiliation) {
      const resiliationFields = [
        "project.fields.replaceContract",
        "project.fields.oldContractYear",
        "project.fields.selfOccupation",
        "project.terminationRequest.dueDate",
        "project.terminationRequest.contractNumber",
        "project.terminationRequest.recipient",
        "project.terminationRequest.reason",
      ];

      const basicErrors = Object.keys(formProps.errors).filter(
        (key) => !resiliationFields.some((field) => key.includes(field)),
      );

      const result = basicErrors.length === 0;
      return result;
    }

    if (
      resiliation &&
      selfOccupation === true &&
      get(values, "project.ria") === true
    ) {
      return false;
    }

    if (resiliation && replaceContract === false) return !hasErrors;

    if (resiliation && replaceContract === true) {
      if (hasOldContractMoreThanYear === null) return false;

      if (hasOldContractMoreThanYear === false) return false;

      if (hasOldContractMoreThanYear === true) {
        if (selfOccupation === true) return !hasErrors;
        if (
          selfOccupation === false &&
          dueDate &&
          contractNumber &&
          currentCompany &&
          reason
        ) {
          return !hasErrors;
        }

        return false;
      }
    }

    return false;
  };

  const RIA_VALUES = [
    { label: TRANSLATION.FORM.RIA.ENABLED.SELF_OCCUPATION, value: true },
    { label: TRANSLATION.FORM.RIA.ENABLED.AUTO_OCCUPATION, value: false },
  ];

  useEffect(() => {
    formProps.validateForm().then(() => {
      formProps.setTouched({
        ...formProps.touched,
        "project.fields.selfOccupation": true,
      });
    });
  }, [values.project?.fields?.selfOccupation, values.project?.ria]);

  const isMobile = useMediaQuery({ maxWidth: 768 });

  return (
    <>
      <Modal
        open={openHelp}
        onCancel={() => setOpenHelp(false)}
        width={500}
        centered
        footer={null}
        className="LMF-resiliation-modal"
      >
        <h1 className="LMF-resiliation-modal__title">
          {TRANSLATION.RESILIATION_MODAL.TITLE}
        </h1>
        <p className="LMF-resiliation-modal__description">
          {TRANSLATION.RESILIATION_MODAL.DESCRIPTION}
        </p>
        <Button
          className="w-full"
          onClick={() => setOpenHelp(false)}
          type="white"
          size="middle"
        >
          {TRANSLATION.RESILIATION_MODAL.CLOSE}
        </Button>
      </Modal>

      <UpdateCostModal
        showChangeCostModal={false && showChangeCostModal}
        insurancePlanName={get(subscription, "insurancePlan.name")}
        setShowChangeCostModal={setShowChangeCostModal}
        onChangeCost={onChangeCost}
        onCancelChangeCost={onCancelChangeCost}
      />

      <Layout>
        <Head
          description={TRANSLATION.DESCRIPTION}
          title={TRANSLATION.TITLE}
          onBack={onBack}
        />

        <div className="__LMF--content LMF-content lmf__form">
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Input
                label={TRANSLATION.FORM.EFFECTIVE_DATE.LABEL}
                placeholder={TRANSLATION.FORM.EFFECTIVE_DATE.PLACEHOLDER}
                onFocus={() =>
                  setOldIssuanceDate(values?.project?.contract?.issuanceDate)
                }
                className={classNames({
                  valid: get(values, "project.contract.issuanceDate"),
                })}
                type="date"
                variant="simple"
                format={DATE_FORMAT}
                defaultPickerValue={pickerValue}
                disabledDate={(current) => {
                  return (
                    current &&
                    (current < validStartDate || current > validEndDate)
                  );
                }}
                {...bindDateInputProps({
                  name: "project.contract.issuanceDate",
                  values,
                  setFieldValue,
                  ...formProps,
                })}
                onChange={(date) => {
                  setFieldValue("project.contract.issuanceDate", date);
                  if (
                    date.isValid() &&
                    date >= validStartDate &&
                    date <= validEndDate
                  ) {
                    recalculateCost({
                      oldIssuanceDate: oldIssuanceDate,
                      issuanceDate: date,
                      setFieldValue,
                      setShowChangeCostModal,
                    });
                  }
                }}
                suffix={
                  get(formProps.errors, `project.contract.issuanceDate`) ? (
                    <img src={InputError} alt="" />
                  ) : (
                    <img src={InputChecked} alt="" />
                  )
                }
                onBlur={({ target: { value: issuanceDate } }) => {
                  const date = dayjs(issuanceDate, DATE_FORMAT).add(2, "h");
                  if (
                    date.isValid() &&
                    date >= validStartDate &&
                    date <= validEndDate
                  ) {
                    setFieldValue("project.contract.issuanceDate", date);
                  } else setFieldTouched("project.contract.issuanceDate");
                }}
                disabled
              />
            </Col>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Select
                label={TRANSLATION.FORM.BIRTH_COUNTRY.LABEL}
                placeholder={TRANSLATION.FORM.BIRTH_COUNTRY.PLACEHOLDER}
                options={COUNTRIES.map((name) => ({
                  label: name,
                  value: name,
                }))}
                showSearch
                allowClear
                className={classNames({
                  valid: get(
                    values,
                    `telecoms.${CONTACT_POINT_SYSTEM.BIRTH_COUNTRY}.value`,
                  ),
                })}
                {...bindInputProps({
                  name: `telecoms.${CONTACT_POINT_SYSTEM.BIRTH_COUNTRY}.value`,
                  values,
                  setFieldValue,
                  setFieldTouched,
                  ...formProps,
                })}
                onChange={(value) => {
                  setFieldValue(
                    `telecoms.${CONTACT_POINT_SYSTEM.BIRTH_COUNTRY}.value`,
                    value,
                  );
                  if (value !== "France")
                    setFieldValue(
                      `telecoms.${CONTACT_POINT_SYSTEM.DEPARTMENT_OF_BIRTH}.value`,
                      "Autre/Etranger",
                    );
                }}
                suffix={
                  get(
                    formProps.errors,
                    `telecoms.${CONTACT_POINT_SYSTEM.BIRTH_COUNTRY}.value`,
                  ) ? (
                    <img src={InputError} alt="" />
                  ) : (
                    <img src={InputChecked} alt="" />
                  )
                }
              />
            </Col>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Input
                label={TRANSLATION.FORM.BIRTH_CITY.LABEL}
                placeholder={TRANSLATION.FORM.BIRTH_CITY.PLACEHOLDER}
                className={classNames({
                  valid: get(
                    values,
                    `telecoms.${CONTACT_POINT_SYSTEM.BIRTH_CITY}.value`,
                  ),
                })}
                {...bindInputProps({
                  name: `telecoms.${CONTACT_POINT_SYSTEM.BIRTH_CITY}.value`,
                  values,
                  setFieldValue,
                  setFieldTouched,
                  ...formProps,
                })}
                onChange={({ target: { value } }) => {
                  const filteredValue = filterCityName(value);
                  setFieldValue(
                    `telecoms.${CONTACT_POINT_SYSTEM.BIRTH_CITY}.value`,
                    filteredValue,
                  );
                }}
                onBlur={({ target: { value } }) => {
                  const trimmedValue = value.trim();
                  setFieldValue(
                    `telecoms.${CONTACT_POINT_SYSTEM.BIRTH_CITY}.value`,
                    trimmedValue,
                  );
                  setFieldTouched(
                    `telecoms.${CONTACT_POINT_SYSTEM.BIRTH_CITY}.value`,
                    true,
                  );
                }}
                suffix={
                  get(
                    formProps.errors,
                    `telecoms.${CONTACT_POINT_SYSTEM.BIRTH_CITY}.value`,
                  ) ? (
                    <img src={InputError} alt="" />
                  ) : (
                    <img src={InputChecked} alt="" />
                  )
                }
              />
            </Col>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Select
                label={TRANSLATION.FORM.DEPARTMENT_OF_BIRTH.LABEL}
                placeholder={TRANSLATION.FORM.DEPARTMENT_OF_BIRTH.PLACEHOLDER}
                showSearch
                allowClear
                options={FRANCE_DEPARTMENTS.map(({ code, name }) => ({
                  label: `${code && `(${code})`} ${name}`,
                  value: name,
                }))}
                className={classNames({
                  valid: get(
                    values,
                    `telecoms.${CONTACT_POINT_SYSTEM.DEPARTMENT_OF_BIRTH}.value`,
                  ),
                })}
                {...bindInputProps({
                  name: `telecoms.${CONTACT_POINT_SYSTEM.DEPARTMENT_OF_BIRTH}.value`,
                  values,
                  setFieldValue,
                  setFieldTouched,
                  ...formProps,
                })}
                onChange={(value) => {
                  setFieldValue(
                    `telecoms.${CONTACT_POINT_SYSTEM.DEPARTMENT_OF_BIRTH}.value`,
                    value,
                  );
                }}
                suffix={
                  get(
                    formProps.errors,
                    `telecoms.${CONTACT_POINT_SYSTEM.DEPARTMENT_OF_BIRTH}.value`,
                  ) ? (
                    <img src={InputError} alt="" />
                  ) : (
                    <img src={InputChecked} alt="" />
                  )
                }
              />
            </Col>
          </Row>
          <div className="lmfPayment__section--refund-container mt-24">
            <div className="lmfPayment__section--refund-container--action">
              <div className="lmfPayment__section--refund-container--action__left">
                <span>{TRANSLATION.FORM.RIA.LABEL}</span>
              </div>
              <div className="lmfPayment__section--refund-container--right">
                <div className="d-flex">
                  <Button
                    size="middle"
                    type={classNames({
                      primary: resiliation === true,
                      white: resiliation === false,
                    })}
                    onClick={() => {
                      if (locked) return;
                      RIA_STATUS[true]();
                      setResiliation(true);
                      setFieldValue("resiliation", true);
                      setHasOldContract(false);

                      setReplaceContract(null);
                      setHasOldContractMoreThanYear(null);
                      setSelfOccupation(true);
                      setOccupation(true);

                      setFieldValue(
                        "project.fields.replaceContract",
                        undefined,
                      );
                      setFieldValue(
                        "project.fields.oldContractYear",
                        undefined,
                      );
                      setFieldValue("project.fields.selfOccupation", undefined);
                      setFieldValue("project.ria", false);
                      setFieldValue("project.terminationRequest", undefined);
                    }}
                  >
                    {TRANSLATION.YES}
                  </Button>
                  <Button
                    size="middle"
                    type={classNames({
                      primary: resiliation === false,
                      white: resiliation === true,
                    })}
                    onClick={async () => {
                      if (locked) return;
                      RIA_STATUS[false]();

                      const errorFields = [
                        "project.fields.replaceContract",
                        "project.fields.oldContractYear",
                        "project.fields.selfOccupation",
                        "project.terminationRequest.dueDate",
                        "project.terminationRequest.contractNumber",
                        "project.terminationRequest.recipient",
                        "project.terminationRequest.reason",
                      ];

                      errorFields.forEach((field) => {
                        formProps.setFieldError(field, undefined);
                      });

                      await setFieldValue("resiliation", false);
                      await setFieldValue(
                        "project.fields.replaceContract",
                        undefined,
                      );
                      await setFieldValue(
                        "project.fields.oldContractYear",
                        undefined,
                      );
                      await setFieldValue(
                        "project.fields.selfOccupation",
                        undefined,
                      );
                      await setFieldValue("project.ria", false);
                      await setFieldValue(
                        "project.terminationRequest",
                        undefined,
                      );

                      setResiliation(false);
                      setHasOldContract(false);
                      setReplaceContract(null);
                      setHasOldContractMoreThanYear(null);
                      setSelfOccupation(true);
                      setOccupation(true);

                      await formProps.validateForm();

                      setTimeout(() => {
                        formProps.validateForm();
                      }, 100);
                    }}
                  >
                    {TRANSLATION.NO}
                  </Button>
                </div>
              </div>
            </div>

            {resiliation && (
              <>
                <div className="lmf__question">
                  <Actions
                    variant={"normal"}
                    label={TRANSLATION.FORM.RIA.REPLACE_CONTRACT_LABEL}
                    actions={
                      <>
                        <Button
                          size="small"
                          type={classNames({
                            primary: replaceContract === true,
                            white: replaceContract !== true,
                          })}
                          onClick={() => {
                            setReplaceContract(true);
                            setHasOldContractMoreThanYear(null);
                            setFieldValue(
                              "project.fields.replaceContract",
                              true,
                            );
                          }}
                        >
                          {TRANSLATION.YES}
                        </Button>
                        <Button
                          size="small"
                          type={classNames({
                            primary: replaceContract === false,
                            white: replaceContract !== false,
                          })}
                          onClick={() => {
                            setReplaceContract(false);
                            setHasOldContractMoreThanYear(null);
                            setHelpTrigger("replaceContract");
                            setHelpContext("cumul");
                            setOpenHelpModal(true);
                            setFieldValue(
                              "project.fields.replaceContract",
                              true,
                            );
                          }}
                        >
                          {TRANSLATION.NO}
                        </Button>
                      </>
                    }
                  />
                </div>
                {replaceContract === true && (
                  <div className="lmf__question">
                    <Actions
                      variant={"normal"}
                      label={TRANSLATION.FORM.RIA.OLD_CONTRACT_LABEL}
                      actions={
                        <>
                          <Button
                            size="small"
                            type={classNames({
                              primary: hasOldContractMoreThanYear === true,
                              white: hasOldContractMoreThanYear !== true,
                            })}
                            onClick={() => {
                              setHasOldContractMoreThanYear(true);
                              setSelfOccupation(true);
                              setFieldValue(
                                "project.fields.oldContractYear",
                                true,
                              );
                              setFieldValue(
                                "project.fields.selfOccupation",
                                true,
                              );
                            }}
                          >
                            Oui
                          </Button>
                          <Button
                            size="small"
                            type={classNames({
                              primary: hasOldContractMoreThanYear === false,
                              white: hasOldContractMoreThanYear !== false,
                            })}
                            onClick={() => {
                              setHasOldContractMoreThanYear(false);
                              setHelpTrigger("hasOldContractMoreThanYear");
                              setHelpContext("contract");
                              setOpenHelpModal(true);
                              setFieldValue(
                                "project.fields.oldContractYear",
                                false,
                              );
                            }}
                          >
                            Non
                          </Button>
                        </>
                      }
                    />

                    {replaceContract === true &&
                      hasOldContractMoreThanYear === true && (
                        <div className="lmf__question--block">
                          <Radio
                            type={"simple"}
                            direction={"column"}
                            name="project.fields.selfOccupation"
                            title={TRANSLATION.FORM.RIA.ENABLED.TITLE}
                            values={RIA_VALUES}
                            value={get(values, "project.fields.selfOccupation")}
                            onChange={async (val) => {
                              await setFieldValue(
                                "project.fields.selfOccupation",
                                val,
                              );
                              await setFieldTouched(
                                "project.fields.selfOccupation",
                                true,
                              );

                              setSelfOccupation(val);
                              setFieldValue("project.ria", !val);

                              if (val) {
                                setFieldValue(
                                  "project.terminationRequest",
                                  undefined,
                                );
                              }
                              setOccupation(val);
                              await formProps.validateForm();
                            }}
                            errors={get(
                              formProps.errors,
                              "project.fields.selfOccupation",
                            )}
                            touched={get(
                              formProps.touched,
                              "project.fields.selfOccupation",
                            )}
                          />

                          {get(values, "project.ria") &&
                            !get(values, "project.fields.selfOccupation") && (
                              <div className="__LMF--content__body--container__body">
                                <Row gutter={[16, 16]}>
                                  <Col xs={24} sm={24} md={12} lg={12}>
                                    <Input
                                      label={
                                        TRANSLATION.FORM.CURRENT_EFFECTIVE_DATE
                                          .LABEL
                                      }
                                      placeholder={
                                        TRANSLATION.FORM.CURRENT_EFFECTIVE_DATE
                                          .PLACEHOLDER
                                      }
                                      type="date"
                                      variant="simple"
                                      format={DATE_FORMAT}
                                      defaultPickerValue={dayjs().add(
                                        -1,
                                        "day",
                                      )}
                                      disabledDate={(current) => {
                                        return current && current > dayjs();
                                      }}
                                      {...bindDateInputProps({
                                        name: `project.terminationRequest.dueDate`,
                                        values,
                                        setFieldValue,
                                        ...formProps,
                                      })}
                                      disabled={locked}
                                    />
                                  </Col>
                                  <Col xs={24} sm={24} md={12} lg={12}>
                                    <Input
                                      label={
                                        TRANSLATION.FORM.CURRENT_CONTRACT.LABEL
                                      }
                                      placeholder={
                                        TRANSLATION.FORM.CURRENT_CONTRACT
                                          .PLACEHOLDER
                                      }
                                      {...bindInputProps({
                                        name: `project.terminationRequest.contractNumber`,
                                        values,
                                        setFieldValue,
                                        setFieldTouched,
                                        ...formProps,
                                      })}
                                      onChange={({ target: { value } }) => {
                                        const filteredValue =
                                          filterContractNumber(value);
                                        setFieldValue(
                                          `project.terminationRequest.contractNumber`,
                                          filteredValue,
                                        );
                                      }}
                                      onBlur={({ target: { value } }) => {
                                        const trimmedValue = value.trim();
                                        setFieldValue(
                                          `project.terminationRequest.contractNumber`,
                                          trimmedValue,
                                        );
                                        setFieldTouched(
                                          `project.terminationRequest.contractNumber`,
                                          true,
                                        );
                                      }}
                                      disabled={get(subscription, "locked")}
                                    />
                                  </Col>
                                  <Col xs={24} sm={24} md={12} lg={12}>
                                    <Select
                                      type="async"
                                      node={{
                                        query: TERMINATION_RECIPIENTS,
                                        variables: {
                                          where: { id: "54" },
                                        },
                                        accessors: {
                                          root: "terminationRecipients.data",
                                          label: "marque",
                                          value: "id",
                                        },
                                      }}
                                      label={
                                        TRANSLATION.FORM.CURRENT_COMPANY.LABEL
                                      }
                                      placeholder={
                                        TRANSLATION.FORM.CURRENT_COMPANY
                                          .PLACEHOLDER
                                      }
                                      {...bindInputProps({
                                        name: `project.terminationRequest.recipient`,
                                        values,
                                        setFieldValue,
                                        setFieldTouched,
                                        ...formProps,
                                      })}
                                      disabled={locked}
                                    />
                                  </Col>
                                  <Col xs={24} sm={24} md={12} lg={12}>
                                    <Select
                                      type="async"
                                      node={{
                                        query: TERMINATION_MOTIFS,
                                        variables: {},
                                        accessors: {
                                          root: "terminationMotifs.data",
                                          label: "nom",
                                          value: "id",
                                        },
                                      }}
                                      value={get(
                                        values,
                                        "project.terminationRequest.reason",
                                      )}
                                      label={
                                        TRANSLATION.FORM.TERMINATION_MOTIFS
                                          .LABEL
                                      }
                                      placeholder={
                                        TRANSLATION.FORM.TERMINATION_MOTIFS
                                          .PLACEHOLDER
                                      }
                                      {...bindInputProps({
                                        name: `project.terminationRequest.reason`,
                                        values,
                                        setFieldValue,
                                        setFieldTouched,
                                        ...formProps,
                                      })}
                                      disabled={locked}
                                    />
                                  </Col>
                                </Row>
                                <div className="pt-20" />
                                <InfoBox
                                  variant="warning"
                                  text={TRANSLATION.FORM.CAPTION}
                                  icon={<HelpLight size={48} />}
                                />
                              </div>
                            )}
                        </div>
                      )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <ActionButtons
          disabled={!canContinue()}
          onNext={handleSubmit}
          buttonText={TRANSLATION.NEXT}
          {...(isMobile && { onBack })}
          className={classNames({
            "w-100": isMobile,
          })}
          submitting={submitting}
        />
      </Layout>

      <HelpModal
        open={openHelpModal}
        setOpen={(value) => {
          setOpenHelpModal(value);
          if (!value) {
            if (helpTrigger === "replaceContract") {
              setReplaceContract(null);
            }
            if (helpTrigger === "hasOldContractMoreThanYear") {
              setHasOldContractMoreThanYear(null);
            }
          }
        }}
        project={project}
        context={helpContext}
        trigger={helpTrigger}
        onSubmitModal={(confirmed) => {
          setOpenHelpModal(false);
          if (!confirmed) {
            if (helpTrigger === "replaceContract") {
              setReplaceContract(null);
            }
            if (helpTrigger === "hasOldContractMoreThanYear") {
              setHasOldContractMoreThanYear(null);
            }
          }
        }}
      />
    </>
  );
};

export default View;
